import {
  type AiLanguagePracticePage,
  aiLanguagePracticePages,
  getAiTutorPath,
  getSpeakingPracticePath,
} from "./aiLanguagePracticePages";
import {
  type DuolingoAlternativePage,
  duolingoAlternativePages,
  getDuolingoAlternativePagePath,
} from "./duolingoAlternativePages";
import {
  type FillInTheGapLanguagePage,
  fillInTheGapLanguagePages,
} from "./fillInTheGapLanguagePages";
import { getLanguagePairPage } from "./languagePairPages";
import { getLanguageTargetPage } from "./languageTargetPages";

export type SeoPracticeLinks = {
  aiHubPath: string;
  aiTutorPath: string | null;
  duolingoAlternativePath: string | null;
  fillInTheGapPath: string | null;
  languageTargetPath: string | null;
  learnFromEnglishPath: string | null;
  levelTestPath: string | null;
  speakingPracticePath: string | null;
  writingPracticePath: string | null;
};

function normalizeName(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['\u2019]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function findAiPracticePage(
  languageName: string,
  languageCode?: string
): AiLanguagePracticePage | undefined {
  if (languageCode) {
    return aiLanguagePracticePages.find((page) => page.code === languageCode);
  }

  const normalized = normalizeName(languageName);
  return aiLanguagePracticePages.find((page) => normalized.includes(normalizeName(page.name)));
}

function findToolLanguagePage(
  languageName: string,
  languageCode?: string
): FillInTheGapLanguagePage | undefined {
  if (languageCode) {
    return fillInTheGapLanguagePages.find((page) => page.code === languageCode);
  }

  const normalized = normalizeName(languageName);
  return fillInTheGapLanguagePages.find((page) => {
    const pageName = normalizeName(page.name);
    return (
      normalized === pageName || normalized.includes(pageName) || pageName.includes(normalized)
    );
  });
}

function findDuolingoAlternativePage(languageName: string): DuolingoAlternativePage | undefined {
  const normalized = normalizeName(languageName);

  return duolingoAlternativePages.find((page) => {
    const pageName = normalizeName(page.languageName);
    return (
      normalized === pageName || normalized.includes(pageName) || pageName.includes(normalized)
    );
  });
}

export function getSeoPracticeLinksForLanguage(
  languageName: string,
  languageCode?: string
): SeoPracticeLinks {
  const aiPage = findAiPracticePage(languageName, languageCode);
  const toolPage = findToolLanguagePage(languageName, languageCode);
  const duolingoAlternativePage = findDuolingoAlternativePage(languageName);
  const languageTargetPage = toolPage ? getLanguageTargetPage(toolPage.slug) : null;
  const learnFromEnglishPage = aiPage
    ? getLanguagePairPage(aiPage.slug, "english-united-states")
    : null;

  return {
    aiHubPath: "/ai",
    aiTutorPath: aiPage ? getAiTutorPath(aiPage) : null,
    duolingoAlternativePath: duolingoAlternativePage
      ? getDuolingoAlternativePagePath(duolingoAlternativePage)
      : null,
    fillInTheGapPath: toolPage ? `/tools/fill-in-the-gap-language-practice/${toolPage.slug}` : null,
    languageTargetPath: languageTargetPage?.path ?? null,
    learnFromEnglishPath: learnFromEnglishPage?.path ?? null,
    levelTestPath: toolPage ? `/test-your-level/${toolPage.slug}` : null,
    speakingPracticePath: aiPage ? getSpeakingPracticePath(aiPage) : null,
    writingPracticePath: toolPage
      ? `/tools/writing-practice-language-learning/${toolPage.slug}`
      : null,
  };
}
